import Navbar from './Navbar';
import Reports from './Reports';
import GoalLineReportGenerator from './GoalLineReportGenerator';
import FinalDownsReportGenerator from './FinalDownsReportGenerator';
import PassingTargetsReportGenerator from './PassingTargetsReportGenerator';
import DefensiveVulnerabilityReportGenerator from './DefensiveVulnerabilityReportGenerator';
import TwoMinuteReportGenerator from './TwoMinuteReportGenerator';
import RedZoneReportGenerator from './RedZoneReportGenerator';
import BaseDownsReportGenerator from './BaseDownsReportGenerator';
import VanGoBaseDownsReportGenerator from './VanGoBaseDownsReportGenerator';
import VanGoFinalDownsReportGenerator from './VanGoFinalDownsReportGenerator';
import VanGoTwoMinutesReportGenerator from './VanGoTwoMinutesReportGenerator';
import AdminDashboard from './AdminDashboard';
import ReportPermissions from './ReportPermissions';
import ChangePassword from './ChangePassword';

import React, { useState, useEffect } from 'react';
import logger from './logger';
import './App.css';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import domainConfig from './config/domainConfig';

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate
} from 'react-router-dom';

// API Configuration - now uses domain-specific config
const API_BASE_URL = domainConfig.apiUrl;

// Helper function to get token from cookie
const getTokenFromCookie = () => {
  const match = document.cookie.match(/(?:^|; )auth-token=([^;]*)/);
  return match ? match[1] : null;
};

// Helper function to make authenticated requests
const apiRequest = async (url, options = {}) => {
  const token = getTokenFromCookie();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };
  
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers,
    credentials: 'include'
  });
};

const Login = ({ setUser }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};
    
    if (!username.trim()) {
      newErrors.username = 'Username is required';
    }
    
    if (!password) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      logger.log('handleLogin');
      const res = await apiRequest('/api/login', {
        method: 'POST',
        body: JSON.stringify({ username: username.trim(), password })
      });

      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
        navigate('/dashboard');
      } else {
        const errorData = await res.json().catch(() => ({}));
        if (res.status === 403) {
          alert('Your account is pending admin approval. Please wait for approval before logging in.');
        } else {
          alert(errorData.message || 'Invalid username or password');
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      alert('Network error. Please try again.');
    }
  };

  return (
    <Box component="form" onSubmit={handleLogin} sx={{ maxWidth: 320, mx: 'auto', mt: 6, p: 3, boxShadow: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
      <Typography variant="h5" align="center" gutterBottom>Login</Typography>
      <TextField
        label="Username"
        variant="outlined"
        margin="normal"
        fullWidth
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        error={!!errors.username}
        helperText={errors.username}
      />
      <TextField
        label="Password"
        type="password"
        variant="outlined"
        margin="normal"
        fullWidth
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={!!errors.password}
        helperText={errors.password}
      />
      <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2 }}>
        Login
      </Button>
    </Box>
  );
};

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  
  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Welcome, {user.username}!
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Welcome to your dashboard. You can access reports, manage your account, and more.
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          onClick={() => navigate('/reports')}
          sx={{ mb: 2 }}
        >
          View Reports
        </Button>
        
        <Button
          variant="outlined"
          onClick={() => navigate('/change-password')}
          sx={{ mb: 2 }}
        >
          Change Password
        </Button>
        
        {user?.isAdmin && (
          <Button
            variant="outlined"
            onClick={() => navigate('/admin')}
            sx={{ mb: 2 }}
          >
            Admin Dashboard
          </Button>
        )}
      </Box>
    </Box>
  );
};

const RegisterUser = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [teamLevel, setTeamLevel] = useState('');
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};
    
    if (!firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!emailAddress.trim()) {
      newErrors.emailAddress = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddress)) {
      newErrors.emailAddress = 'Please enter a valid email address';
    }
    
    if (!username.trim()) {
      newErrors.username = 'Username is required';
    } else if (username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      newErrors.username = 'Username can only contain letters, numbers, and underscores';
    }
    
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    if (!teamLevel) {
      newErrors.teamLevel = 'Team level is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegisterUser = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const res = await apiRequest('/api/register', {
        method: 'POST',
        body: JSON.stringify({ 
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          emailAddress: emailAddress.trim(),
          username: username.trim(), 
          password,
          teamLevel
        })
      });

      if (res.ok) {
        const data = await res.json();
        alert(data.message || 'Registration request submitted! Please wait for admin approval before logging in.');
        navigate('/login');
      } else {
        const errorData = await res.json().catch(() => ({}));
        alert(errorData.message || 'Registration failed. Please try again.');
      }
    } catch (err) {
      console.error('Registration error:', err);
      alert('Network error. Please try again.');
    }
  };

  return (
    <Box component="form" onSubmit={handleRegisterUser} sx={{ maxWidth: 400, mx: 'auto', mt: 6, p: 3, boxShadow: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
      <Typography variant="h5" align="center" gutterBottom>Register</Typography>
      <TextField
        label="First Name"
        variant="outlined"
        margin="normal"
        fullWidth
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        error={!!errors.firstName}
        helperText={errors.firstName}
      />
      <TextField
        label="Last Name"
        variant="outlined"
        margin="normal"
        fullWidth
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
        error={!!errors.lastName}
        helperText={errors.lastName}
      />
      <TextField
        label="Email Address"
        type="email"
        variant="outlined"
        margin="normal"
        fullWidth
        value={emailAddress}
        onChange={(e) => setEmailAddress(e.target.value)}
        error={!!errors.emailAddress}
        helperText={errors.emailAddress}
      />
      <TextField
        label="Username"
        variant="outlined"
        margin="normal"
        fullWidth
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        error={!!errors.username}
        helperText={errors.username}
      />
      <TextField
        label="Password"
        type="password"
        variant="outlined"
        margin="normal"
        fullWidth
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={!!errors.password}
        helperText={errors.password}
      />
      <FormControl
        variant="outlined"
        margin="normal"
        fullWidth
        error={!!errors.teamLevel}
      >
        <InputLabel>Team Level</InputLabel>
        <Select
          value={teamLevel}
          onChange={(e) => setTeamLevel(e.target.value)}
          label="Team Level"
        >
          <MenuItem value="High School">High School</MenuItem>
          <MenuItem value="College D3">College D3</MenuItem>
          <MenuItem value="College D2">College D2</MenuItem>
          <MenuItem value="College FCS">College FCS</MenuItem>
          <MenuItem value="College FBS">College FBS</MenuItem>
        </Select>
        {errors.teamLevel && <FormHelperText>{errors.teamLevel}</FormHelperText>}
      </FormControl>
      <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2 }}>
        Register
      </Button>
    </Box>
  );
};

const ProtectedRoute = ({ user, children }) => {
  return user ? children : <Navigate to="/login" />;
};

function App() {
  const [user, setUser] = useState(null);

  const fetchUser = async () => {
    try {
      const token = getTokenFromCookie();
      if (!token) {
        setUser(null);
        return;
      }

      const res = await apiRequest('/api/me');

      if (res.ok) {
        const data = await res.json();
        console.log('🔍 App.js - /api/me response:', data);
        console.log('🔍 App.js - User data from API:', data.user);
        setUser({
          ...data.user,
          isAdmin: data.user.role === 'admin' || data.user.isAdmin
        });
      } else {
        console.log('❌ App.js - /api/me failed:', res.status, res.statusText);
        setUser(null);
      }
    } catch (err) {
      console.error('Session check failed:', err);
      setUser(null);
    }
  };

  const handleLogout = async () => {
    try {
      logger.log('logging out');
      await apiRequest('/api/logout', {
        method: 'POST'
      });
    } catch (err) {
      console.error('Logout failed:', err);
    } finally {
      setUser(null);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  return (
    <Router>
      <Navbar user={user} onLogout={handleLogout} />
      <Routes>
        <Route path="/login" element={<Login setUser={setUser} />} />
        <Route path="/register" element={<RegisterUser />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute user={user}>
              <Dashboard user={user} />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to={user ? '/dashboard' : '/login'} />} />
        <Route path="/reports"
          element={
            <ProtectedRoute user={user}>
              <Reports user={user} />
            </ProtectedRoute>
          }
        />
        <Route path="/goalline"
          element={
            <ProtectedRoute user={user}>
              <GoalLineReportGenerator />
            </ProtectedRoute>
          }
        />
        <Route path="/finaldowns"
          element={
            <ProtectedRoute user={user}>
              <FinalDownsReportGenerator />
            </ProtectedRoute>
          }
        />
        <Route path="/passingtargets"
          element={
            <ProtectedRoute user={user}>
              <PassingTargetsReportGenerator />
            </ProtectedRoute>
          }
        />
        <Route path="/defensivevulnerability"
          element={
            <ProtectedRoute user={user}>
              <DefensiveVulnerabilityReportGenerator />
            </ProtectedRoute>
          }
        />
        <Route path="/twominute"
          element={
            <ProtectedRoute user={user}>
              <TwoMinuteReportGenerator />
            </ProtectedRoute>
          }
        />
        <Route path="/redzone"
          element={
            <ProtectedRoute user={user}>
              <RedZoneReportGenerator />
            </ProtectedRoute>
          }
        />
        <Route path="/basedowns"
          element={
            <ProtectedRoute user={user}>
              <BaseDownsReportGenerator />
            </ProtectedRoute>
          }
        />
        <Route path="/vangobasedowns"
          element={
            <ProtectedRoute user={user}>
              <VanGoBaseDownsReportGenerator />
            </ProtectedRoute>
          }
        />
        <Route path="/vangofinaldowns"
          element={
            <ProtectedRoute user={user}>
              <VanGoFinalDownsReportGenerator />
            </ProtectedRoute>
          }
        />
        <Route path="/vangotwominutes"
          element={
            <ProtectedRoute user={user}>
              <VanGoTwoMinutesReportGenerator />
            </ProtectedRoute>
          }
        />
        <Route path="/admin"
          element={
            <ProtectedRoute user={user}>
              <AdminDashboard user={user} apiRequest={apiRequest} />
            </ProtectedRoute>
          }
        />
        <Route path="/admin/report-permissions"
          element={
            <ProtectedRoute user={user}>
              <ReportPermissions user={user} apiRequest={apiRequest} />
            </ProtectedRoute>
          }
        />
        <Route path="/change-password"
          element={
            <ProtectedRoute user={user}>
              <ChangePassword user={user} />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
export { apiRequest };